import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, ArrowLeft, FileText, Trash2, Download, RefreshCw, Send, Eye, X, Ban } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD, fmtDate, statusClass, todayIso } from "../lib/format";
import { LineItemsEditor } from "../components/LineItemsEditor";
import SendEmailDialog from "../components/SendEmailDialog";
import CustomerPicker from "../components/CustomerPicker";
import ConfirmDialog from "../components/ConfirmDialog";

function getExpirationPreset(dateStr) {
  if (!dateStr) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days === 7) return "7";
  if (days === 14) return "14";
  if (days === 30) return "30";
  if (days === 60) return "60";
  if (days === 90) return "90";
  return "custom";
}

const STATUS = ["Draft", "Sent", "Viewed", "Approved", "Declined", "Expired"];

export function EstimatesList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [dlg, setDlg] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    const [{ data }, { data: custs }] = await Promise.all([api.get(`/estimates?${params}`), api.get("/customers")]);
    setItems(data);
    setCustomers(Object.fromEntries(custs.map((c) => [c.id, c])));
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [status]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const batchDelete = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Delete ${selected.size} estimate${selected.size > 1 ? "s" : ""}?`,
      body: "This permanently removes the selected estimate(s) from the list.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/estimates/batch-delete", { ids: Array.from(selected) });
          alert(`${data.deleted} estimate(s) deleted.`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  const batchCancel = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Cancel ${selected.size} estimate${selected.size > 1 ? "s" : ""}?`,
      body: "They will be marked Declined and remain in history.",
      confirmLabel: "Cancel estimates",
      danger: false,
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/estimates/batch-cancel", { ids: Array.from(selected) });
          alert(`${data.cancelled} estimate(s) cancelled.`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  return (
    <div data-testid="estimates-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Estimates</h1><p className="text-sm text-slate-500 mt-1">{items.length} estimate{items.length !== 1 ? "s" : ""}{selected.size > 0 && ` · ${selected.size} selected`}</p></div>
        <Link to="/estimates/new" data-testid="new-estimate-btn" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> New Estimate</Link>
      </div>
      <div className="flex gap-3 flex-wrap items-center">
        <select data-testid="estimate-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
          <option value="all">All statuses</option>
          {STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        {selected.size > 0 && (
          <div data-testid="estimates-batch-bar" className="flex items-center gap-2 px-3 py-1.5 bg-[#E0F2FE] border border-[#0F4C81]/30 rounded-md">
            <span className="text-xs font-medium text-[#0F4C81]">{selected.size} selected</span>
            <button onClick={batchCancel} disabled={batchBusy} data-testid="estimates-batch-cancel-btn" className="px-2.5 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded flex items-center gap-1 disabled:opacity-50"><Ban size={11}/>Cancel</button>
            <button onClick={batchDelete} disabled={batchBusy} data-testid="estimates-batch-delete-btn" className="px-2.5 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1 disabled:opacity-50"><Trash2 size={11}/>Delete</button>
            <button onClick={() => setSelected(new Set())} className="px-2.5 py-1 text-xs border border-slate-300 text-slate-700 rounded hover:bg-white">Clear</button>
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="estimates-select-all" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll}/></th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Number</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Date</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Expires</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No estimates yet.</td></tr>}
              {items.map((e) => (
                <tr key={e.id} data-testid={`estimate-row-${e.id}`} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(e.id) ? "bg-blue-50/40" : ""}`} onClick={() => window.location.href = `/estimates/${e.id}`}>
                  <td className="px-3 py-3" onClick={(ev) => { ev.stopPropagation(); toggle(e.id); }}>
                    <input type="checkbox" data-testid={`estimate-select-${e.id}`} checked={selected.has(e.id)} onChange={() => toggle(e.id)}/>
                  </td>
                  <td className="px-4 py-3 font-medium font-mono">{e.number}</td>
                  <td className="px-4 py-3">{customers[e.customer_id]?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{fmtDate(e.expiration_date)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusClass(e.status)}`}>{e.status}</span></td>
                  <td className="px-4 py-3 text-right font-medium tabular">{fmtCAD(e.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

export function EstimateForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [hstRate, setHstRate] = useState(13);
  const [data, setData] = useState({
    customer_id: params.get("customer") || "",
    line_items: [],
    invoice_discount_type: "amount",
    invoice_discount_value: 0,
    expiration_date: "",
    notes: "",
    status: "Draft",
  });
  const [existing, setExisting] = useState(null);
  const [err, setErr] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [localDlg, setLocalDlg] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/settings").then(({ data }) => setHstRate(data.hst_rate || 13));
    if (!isNew) {
      api.get(`/estimates/${id}`).then(({ data }) => { setData(data); setExisting(data); if (data.customer_id) api.get(`/customers/${data.customer_id}`).then(({ data: c }) => setCustomer(c)); });
      api.get(`/email-sends?entity_type=estimate&entity_id=${id}`).then(({ data }) => setEmailHistory(data));
    }
  }, [id]);

  const save = async (silent = false) => {
    if (!silent) setSaving(true);
    setErr("");
    try {
      const payload = { ...data, line_items: data.line_items, invoice_discount_value: Number(data.invoice_discount_value) };
      if (isNew) {
        const { data: e } = await api.post("/estimates", payload);
        nav(`/estimates/${e.id}`);
      } else {
        await api.put(`/estimates/${id}`, payload);
        const { data: e } = await api.get(`/estimates/${id}`);
        setData(e); setExisting(e);
        setSavedAt(new Date());
      }
    } catch (e) { if (!silent) setErr(formatErr(e)); }
    finally { if (!silent) setSaving(false); }
  };

  // Auto-save estimate edits 1.5s after last change (existing estimates only)
  useEffect(() => {
    if (isNew || !existing || !data.customer_id) return;
    const same = JSON.stringify(existing.line_items || []) === JSON.stringify(data.line_items || [])
      && Number(existing.invoice_discount_value || 0) === Number(data.invoice_discount_value || 0)
      && (existing.invoice_discount_type || "amount") === (data.invoice_discount_type || "amount")
      && (existing.expiration_date || "") === (data.expiration_date || "")
      && (existing.notes || "") === (data.notes || "")
      && (existing.status || "Draft") === (data.status || "Draft");
    if (same) return;
    const t = setTimeout(() => { save(true); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const convertToInvoice = () => {
    setLocalDlg({
      title: "Convert this estimate to an invoice?",
      body: "A new invoice will be created from this estimate. The estimate remains for reference.",
      confirmLabel: "Convert",
      danger: false,
      onConfirm: async () => {
        try {
          const { data: inv } = await api.post(`/estimates/${id}/convert`);
          nav(`/invoices/${inv.id}`);
        } catch (e) { alert(formatErr(e)); }
      },
    });
  };

  const del = () => {
    setLocalDlg({
      title: `Delete estimate ${data?.number || ""}?`,
      body: "This permanently deletes the estimate. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try { await api.delete(`/estimates/${id}`); nav("/estimates"); }
        catch (e) { alert(formatErr(e)); }
      },
    });
  };

  return (
    <div data-testid="estimate-form" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{isNew ? "New Estimate" : existing?.number}</h1>
            {existing && <span className={`text-xs px-2 py-0.5 rounded ${statusClass(existing.status)}`}>{existing.status}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isNew && <a href={fileUrl(`/estimates/${id}/pdf`)} target="_blank" rel="noopener noreferrer" data-testid="estimate-pdf-btn" className="px-3 py-2 border border-slate-300 dark:border-slate-700 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"><Download size={14} /> PDF</a>}
          {!isNew && <button onClick={() => setShowEmail(true)} data-testid="send-estimate-email-btn" className="px-3 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#E0F2FE] flex items-center gap-2"><Send size={14} /> Send Email</button>}
          {!isNew && existing?.status !== "Approved" && <button onClick={convertToInvoice} data-testid="convert-to-invoice-btn" className="px-3 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-md hover:bg-emerald-50 flex items-center gap-2"><RefreshCw size={14} /> Convert to Invoice</button>}
          {!isNew && <button onClick={del} data-testid="estimate-delete-btn" className="p-2 text-slate-500 hover:text-red-600 border border-slate-300 rounded-md"><Trash2 size={16} /></button>}
          {!isNew && (
            <span className="text-xs text-slate-500 flex items-center gap-1.5 px-2" data-testid="estimate-autosave-status">
              {saving ? "Saving…" : savedAt ? `Saved · ${new Date(savedAt).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}` : "Auto-save on"}
            </span>
          )}
          <button onClick={() => save(false)} data-testid="save-estimate-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-md p-6 grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Customer *</label>
          <div className="mt-1">
            <CustomerPicker
              testid="estimate-customer"
              value={data.customer_id}
              onChange={(id) => setData({ ...data, customer_id: id })}
              required
              customers={customers}
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Expires in</label>
          <select
            data-testid="estimate-expiration-preset"
            value={getExpirationPreset(data.expiration_date)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "custom") {
                // Leave existing date; user can pick below
              } else if (v === "none") {
                setData({ ...data, expiration_date: null, expiration_preset_days: 0 });
              } else {
                const days = parseInt(v);
                const d = new Date();
                d.setDate(d.getDate() + days);
                setData({ ...data, expiration_date: d.toISOString().slice(0, 10), expiration_preset_days: days });
              }
            }}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
          >
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days (default)</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="custom">Custom date</option>
            <option value="none">No expiration</option>
          </select>
          <input
            data-testid="estimate-expiration"
            type="date"
            value={data.expiration_date || ""}
            onChange={(e) => setData({ ...data, expiration_date: e.target.value, expiration_preset_days: null })}
            className="mt-1.5 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
          />
          <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="estimate-expire-reminder"
              checked={data.expire_reminder_enabled !== false}
              onChange={(e) => setData({ ...data, expire_reminder_enabled: e.target.checked })}
            />
            Email customer 3 days before expiration
          </label>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Status</label>
          <select data-testid="estimate-status" value={data.status} onChange={(e) => setData({ ...data, status: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
            {STATUS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <OptionsEditor
        options={data.options || []}
        setOptions={(opts) => setData({ ...data, options: opts })}
        // Legacy fallback for single-option line items
        fallbackLineItems={data.line_items}
        setFallbackLineItems={(li) => setData({ ...data, line_items: li })}
        invDiscType={data.invoice_discount_type}
        setInvDiscType={(t) => setData({ ...data, invoice_discount_type: t })}
        invDiscVal={data.invoice_discount_value}
        setInvDiscVal={(v) => setData({ ...data, invoice_discount_value: v })}
        hstRate={hstRate}
      />

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Notes</label>
        <textarea data-testid="estimate-notes" value={data.notes || ""} onChange={(e) => setData({ ...data, notes: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
      </div>

      {emailHistory.length > 0 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Eye size={16} /> Email Activity</h3>
          <div className="space-y-2">
            {emailHistory.map((e) => (
              <div key={e.id} className="flex justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2">
                <div>
                  <div className="text-sm">Sent to <span className="font-medium">{e.to}</span>{e.stubbed && <span className="text-xs ml-2 text-amber-600">(stubbed — no key)</span>}</div>
                  <div className="text-xs text-slate-500">{fmtDate(e.sent_at)} · {e.subject}</div>
                </div>
                <div className="text-right text-xs">
                  {e.opens > 0 ? (
                    <div className="text-emerald-700">Opened {e.opens}× · last {fmtDate(e.last_opened_at)}</div>
                  ) : (
                    <div className="text-slate-500">Not opened yet</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showEmail && existing && (
        <SendEmailDialog entityType="estimate" entity={existing} customer={customer}
          onSent={() => api.get(`/email-sends?entity_type=estimate&entity_id=${id}`).then(({ data }) => setEmailHistory(data))}
          onClose={() => setShowEmail(false)} />
      )}
      <ConfirmDialog dialog={localDlg} onClose={() => setLocalDlg(null)} />
    </div>
  );
}


/* ---------- Multi-option Estimate Builder (Good / Better / Best) ---------- */

function OptionsEditor({ options, setOptions, fallbackLineItems, setFallbackLineItems, invDiscType, setInvDiscType, invDiscVal, setInvDiscVal, hstRate }) {
  const [activeIdx, setActiveIdx] = useState(0);

  const addOption = () => {
    if (options.length >= 5) return;
    const id = `opt-${Date.now()}`;
    const presets = ["Good", "Better", "Best", "Option D", "Option E"];
    const next = [...options, { id, name: presets[options.length] || `Option ${options.length + 1}`, description: "", line_items: [], discount_type: "amount", discount_value: 0 }];
    setOptions(next);
    setActiveIdx(next.length - 1);
  };

  const removeOption = (idx) => {
    const next = options.filter((_, i) => i !== idx);
    setOptions(next);
    if (activeIdx >= next.length) setActiveIdx(Math.max(0, next.length - 1));
  };

  const updateOption = (idx, patch) => {
    setOptions(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };

  if (options.length === 0) {
    return (
      <div className="space-y-3">
        <LineItemsEditor
          lineItems={fallbackLineItems}
          setLineItems={setFallbackLineItems}
          invDiscType={invDiscType}
          setInvDiscType={setInvDiscType}
          invDiscVal={invDiscVal}
          setInvDiscVal={setInvDiscVal}
          hstRate={hstRate}
        />
        <div className="bg-gradient-to-r from-indigo-50 to-amber-50 border border-indigo-200 rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <Plus size={16}/>
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Offer multiple price options</div>
              <div className="text-xs text-slate-600">Show Good / Better / Best — customers pick what fits their budget. ~30% higher close rates.</div>
            </div>
          </div>
          <button
            type="button"
            onClick={addOption}
            data-testid="add-first-option-btn"
            className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium"
          >
            Convert to multi-option →
          </button>
        </div>
      </div>
    );
  }

  const active = options[activeIdx];
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {options.map((o, i) => {
            const optTotal = o.total != null ? o.total
              : (o.line_items || []).reduce((s, li) => s + (Number(li.quantity || 0) * Number(li.rate || 0)), 0) * (1 + (hstRate || 13) / 100);
            return (
              <button
                key={o.id || i}
                type="button"
                onClick={() => setActiveIdx(i)}
                data-testid={`option-tab-${i}`}
                className={`relative flex-1 min-w-[140px] px-4 py-3 text-left text-sm border-r border-slate-200 ${activeIdx === i ? "bg-white" : "hover:bg-white/50 text-slate-600"}`}
              >
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${activeIdx === i ? "text-[#0F4C81]" : "text-slate-400"}`}>{["Good", "Better", "Best"][i] || `Option ${i + 1}`}</div>
                <div className="font-medium text-slate-900 truncate">{o.name}</div>
                <div className="text-xs text-slate-500 tabular">${optTotal.toFixed(2)}</div>
                {options.length > 1 && activeIdx === i && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalDlg({
                        title: `Remove option ${o.name}?`,
                        body: "This removes the pricing option from this estimate. This cannot be undone.",
                        confirmLabel: "Remove",
                        onConfirm: async () => { removeOption(i); },
                      });
                    }}
                    data-testid={`remove-option-${i}`}
                    className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 rounded"
                  >
                    <X size={12}/>
                  </button>
                )}
                {activeIdx === i && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0F4C81]"/>}
              </button>
            );
          })}
          {options.length < 5 && (
            <button
              type="button"
              onClick={addOption}
              data-testid="add-option-btn"
              className="px-4 py-3 text-sm text-[#0F4C81] hover:bg-blue-50 font-medium border-r border-slate-200 flex items-center gap-1.5 shrink-0"
            >
              <Plus size={14}/> Add option
            </button>
          )}
        </div>

        {active && (
          <div className="px-4 py-3 grid sm:grid-cols-[1fr_2fr] gap-3 border-b border-slate-100">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Option name</label>
              <input
                value={active.name}
                onChange={(e) => updateOption(activeIdx, { name: e.target.value })}
                data-testid={`option-name-${activeIdx}`}
                placeholder="e.g. Good · Repair only"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Customer-facing description</label>
              <input
                value={active.description || ""}
                onChange={(e) => updateOption(activeIdx, { description: e.target.value })}
                data-testid={`option-desc-${activeIdx}`}
                placeholder="e.g. Replace P-trap and reseal. 1-year warranty."
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
          </div>
        )}

        {active && (
          <div className="px-2 py-3">
            <LineItemsEditor
              key={active.id}
              lineItems={active.line_items || []}
              setLineItems={(li) => updateOption(activeIdx, { line_items: li })}
              invDiscType={active.discount_type || "amount"}
              setInvDiscType={(t) => updateOption(activeIdx, { discount_type: t })}
              invDiscVal={active.discount_value || 0}
              setInvDiscVal={(v) => updateOption(activeIdx, { discount_value: v })}
              hstRate={hstRate}
            />
          </div>
        )}
      </div>
      <div className="text-xs text-slate-500 italic">
        Customers will see all {options.length} option{options.length > 1 ? "s" : ""} side-by-side on their approval page and pick one to approve. The approved option becomes the job.
      </div>
    </div>
  );
}
